import { Redirect, Route, Switch, useParams, useRouteMatch } from "react-router-dom";
import {
  CoverageRefreshProvider,
  FayAuthGateProvider,
  FayObservabilityProvider,
  packageRoutePattern,
  useFayAuthFailed,
} from "@jsq/fay-components";
import { ErrorPage } from "~/components/ErrorPage/ErrorPage";
import { Arenas } from "./Arenas/Arenas";
import { ArenaSurface } from "./ArenaSurface/ArenaSurface";

type ArenaRouteParams = {
  id: string;
};

type ArenaRouteProps = {
  basePath: string;
};

type GatedArenaSurfaceProps = {
  arenaId: number;
};

/**
 * The arena surface plus the gate its pollers report into — a provider's value isn't visible to
 * the component that renders the provider, so the read lives one level down. When a poller sees
 * fay return a terminal 401/403, this swaps the surface for the error page rather than leaving
 * each widget to render its own inline "Failed to load…" line.
 */
const GatedArenaSurface = ({ arenaId }: GatedArenaSurfaceProps) => {
  const authFailed = useFayAuthFailed();

  return authFailed ? <ErrorPage /> : <ArenaSurface arenaId={arenaId} />;
};

const ArenaRoute = ({ basePath }: ArenaRouteProps) => {
  const { id } = useParams<ArenaRouteParams>();
  const arenaId = Number(id);

  if (!Number.isInteger(arenaId) || arenaId <= 0) {
    return <Redirect to={basePath} />;
  }

  // The gate lives per arena, not per page: fay authorizes per arena, so a manager fay rejects
  // must not take the manager list — or the next manager — down with it. Keying on the arena
  // gives each one a fresh gate, which matters because the gate latches once tripped. The RUM
  // context sits outside that gate, so moving between arenas retags it instead of tearing it
  // down and rebuilding it.
  return (
    <FayObservabilityProvider surface="canopy" arenaId={arenaId}>
      <FayAuthGateProvider key={arenaId}>
        <CoverageRefreshProvider>
          <GatedArenaSurface arenaId={arenaId} />
        </CoverageRefreshProvider>
      </FayAuthGateProvider>
    </FayObservabilityProvider>
  );
};

export const FayAI = () => {
  const { path } = useRouteMatch();
  const arenaPath = `${path}/arena/:id`;

  return (
    <Switch>
      <Route exact path={path}>
        <Arenas />
      </Route>
      {/* The package segment is listed alongside the arena path rather than left to a prefix
          match, so the surface stays mounted as a package opens and closes while any other
          nested path still falls through to the redirect below. The pattern comes from the
          package so the segment shape can't drift from the paths `CoverageView` writes; the
          arena stays a route param here, since it isn't resolved until `ArenaRoute`. */}
      <Route exact path={[arenaPath, packageRoutePattern(arenaPath)]}>
        <ArenaRoute basePath={path} />
      </Route>
      <Route>
        <Redirect to={path} />
      </Route>
    </Switch>
  );
};
